import { useState, useEffect, useMemo, useCallback } from "react";
import { Card } from "../../../components/ui/card";
import { Switch } from "../../../components/ui/switch";
import { Badge } from "../../../components/ui/badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faBriefcase,
  faUser,
  faClock,
  faUsers,
  faIdCard,
  faExclamationTriangle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { usePermissions } from "../../../hooks";
import { useAuth } from "../../../hooks";
import { toastSuccess, toastError } from "../../../utils/toast";

const MODULE_ICONS = {
  assign_permission: faShieldAlt,
  business_owner: faUser,
  business_seller: faUsers,
  capital: faClock,
  financial_records: faBriefcase,
  notification: faBriefcase,
  payment: faBriefcase,
  permission: faShieldAlt,
  product: faBriefcase,
  product_category: faBriefcase,
  product_type: faBriefcase,
  sales: faBriefcase,
  sales_closeout: faBriefcase,
  shop: faBriefcase,
  stock: faBriefcase,
  system: faShieldAlt,
  user_account: faIdCard,
  user_activity_logs: faClock,
  user_login: faUser,
  user_logs: faClock,
};

const SPECIAL_PERMISSIONS = ["manage_all", "manage_shop_modules"];

const formatModuleLabel = (module) =>
  module
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const PermissionTab = ({ user }) => {
  const { hasPermission } = useAuth();
  const canAssignPermissions = hasPermission(["manage_assign_permission"]);

  const {
    getAvailablePermissions,
    getUserPermissions,
    assignPermission,
    revokePermission,
  } = usePermissions();

  const [activeModule, setActiveModule] = useState(null);

  const [available, setAvailable] = useState(null);
  const [userPermissions, setUserPermissions] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [savingIds, setSavingIds] = useState(new Set());

  const loadData = useCallback(async () => {
    if (!user?.userlogId) return;
    setLoading(true);
    try {
      const [availableRes, userPermsRes] = await Promise.all([
        getAvailablePermissions(),
        getUserPermissions(user.userlogId),
      ]);

      const availablePayload =
        availableRes?.data?.data ?? availableRes?.data ?? null;
      const userPermsPayload =
        userPermsRes?.data?.data ?? userPermsRes?.data ?? null;

      setAvailable(availablePayload);
      setUserPermissions(new Set(userPermsPayload?.permission_names || []));

      const firstModule = availablePayload?.modules?.[0] || null;
      if (!activeModule) setActiveModule(firstModule);
    } catch (err) {
      console.error("Failed to load permissions:", err);
      toastError("Failed to load permissions");
    } finally {
      setLoading(false);
    }
  }, [
    user?.userlogId,
    activeModule,
    getAvailablePermissions,
    getUserPermissions,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isSpecialActive = useMemo(
    () => SPECIAL_PERMISSIONS.some((name) => userPermissions.has(name)),
    [userPermissions],
  );

  const modules = useMemo(() => available?.modules || [], [available]);

  const groupedPermissions = useMemo(
    () => available?.grouped_permissions || {},
    [available],
  );

  const activeModulePermissions = useMemo(() => {
    if (!activeModule) return [];
    return groupedPermissions[activeModule] || [];
  }, [activeModule, groupedPermissions]);

  const handleToggle = async (permission) => {
    if (!canAssignPermissions) return;

    const { permissionId, permission_name } = permission;
    const isOn = userPermissions.has(permission_name);

    if (isOn && permission_name === "manage_all") {
      const confirmed = window.confirm(
        "Revoking Super Admin will remove ALL permissions for this user. Continue?",
      );
      if (!confirmed) return;
    }

    setSavingIds((prev) => new Set(prev).add(permissionId));

    try {
      if (isOn) {
        await revokePermission({
          userlogId: user.userlogId,
          permissionId,
        });

        if (permission_name === "manage_all") {
          setUserPermissions(new Set());
        } else {
          setUserPermissions((prev) => {
            const next = new Set(prev);
            next.delete(permission_name);
            return next;
          });
        }

        toastSuccess(`${permission.display_name} revoked`);
      } else {
        await assignPermission({
          userlogId: user.userlogId,
          permissionId,
        });

        if (permission_name === "manage_all") {
          setUserPermissions(new Set(["manage_all"]));
        } else {
          setUserPermissions((prev) => {
            const next = new Set(prev);
            next.add(permission_name);
            return next;
          });
        }

        toastSuccess(`${permission.display_name} assigned`);
      }
    } catch (err) {
      console.error("Permission toggle failed:", err);
      toastError(err?.message || "Failed to update permission");
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(permissionId);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <Card className="bg-white border border-gray-200 shadow-sm">
        <div className="flex items-center justify-center py-16 gap-3">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-6 w-6 text-blue-600 animate-spin"
          />
          <p className="text-gray-600 text-sm">Loading permissions...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-white border border-gray-200 shadow-sm">
      <div className="p-5 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faShieldAlt}
              className="text-blue-800 h-4 w-4"
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Module permissions
            </h3>
            <p className="text-sm text-gray-600">
              {canAssignPermissions
                ? `Set what ${user.full_name} can do in each module`
                : `Viewing permissions assigned to ${user.full_name}`}
            </p>
          </div>
        </div>
      </div>

      <div className="p-5">
        {!canAssignPermissions && (
          <div className="mb-5 p-3 rounded-lg bg-blue-50 border border-blue-200 flex items-start gap-3">
            <FontAwesomeIcon
              icon={faShieldAlt}
              className="h-4 w-4 text-blue-600 mt-0.5"
            />
            <div className="text-sm text-blue-800">
              <p className="font-semibold">View only</p>
              <p className="text-xs mt-0.5">
                You don't have permission to assign or revoke permissions.
              </p>
            </div>
          </div>
        )}

        {isSpecialActive && (
          <div className="mb-5 p-3 rounded-lg bg-yellow-50 border border-yellow-200 flex items-start gap-3">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="h-4 w-4 text-yellow-600 mt-0.5"
            />
            <div className="text-sm text-yellow-800">
              <p className="font-semibold">
                Global access is active for this user
              </p>
              <p className="text-xs mt-0.5">
                {userPermissions.has("manage_all")
                  ? "Super Admin grants every permission. Revoke it to enable per-module toggles."
                  : "Shop Modules grants all shop-related permissions. Revoke it to enable per-module toggles."}
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-1 mb-5">
          {modules.map((mod) => {
            const perms = groupedPermissions[mod] || [];
            const count = perms.filter((p) =>
              userPermissions.has(p.permission_name),
            ).length;
            const isActive = activeModule === mod;
            const icon = MODULE_ICONS[mod] || faBriefcase;

            return (
              <button
                key={mod}
                onClick={() => setActiveModule(mod)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-blue-800 text-white shadow-sm"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <FontAwesomeIcon icon={icon} className="h-3 w-3" />
                <span>{formatModuleLabel(mod)}</span>
                <span
                  className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                    isActive
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {count}/{perms.length}
                </span>
              </button>
            );
          })}
        </div>

        {activeModule && (
          <div className="space-y-3">
            {activeModulePermissions.length === 0 && (
              <div className="text-center py-8 text-gray-500 text-sm">
                No permissions for this module
              </div>
            )}

            {activeModulePermissions.map((perm) => {
              const isChecked = userPermissions.has(perm.permission_name);
              const isSaving = savingIds.has(perm.permissionId);
              const isSpecial = SPECIAL_PERMISSIONS.includes(
                perm.permission_name,
              );
              const disabled =
                !canAssignPermissions ||
                (isSpecialActive && !isSpecial) ||
                isSaving;

              return (
                <div
                  key={perm.permissionId}
                  className={`flex items-center justify-between px-4 py-3 border rounded-lg transition-colors ${
                    isSpecial
                      ? "border-blue-200 bg-blue-50"
                      : "border-gray-200 hover:bg-gray-50"
                  } ${disabled && !isSaving ? "opacity-60" : ""}`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-bold ${
                        isChecked
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {perm.permission_name.startsWith("manage") ? "M" : "R"}
                    </span>
                    <div>
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        {perm.display_name}
                        {isSpecial && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-blue-100 text-blue-800 border-blue-200"
                          >
                            Special
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-600">
                        {perm.description || perm.permission_name}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {isSaving && (
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="h-3.5 w-3.5 text-blue-600 animate-spin"
                      />
                    )}
                    {canAssignPermissions && (
                      <Switch
                        checked={isChecked}
                        disabled={disabled}
                        onCheckedChange={() => handleToggle(perm)}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};

export default PermissionTab;
